import { getAsset } from "@/api/assets/assetsApi";
import { COLORS } from "@/constants/colors";
import { convertTimeRange } from "@/helpers/time";
import type {
  ColorScaleMethod,
  ColorScaleSettings,
  Dataset,
  DatasetDetailSchemaResponse,
  DatasetFilter,
  DatasetStyle,
  EColorNames,
  StyleConfig
} from "@/types/dataset";
import { EFilterOptionType } from "@/types/dataset";
import type {
  ActiveLegendType,
  AnalysisDatasetLegendConfig,
  DatasetConfigAttributeResponse,
  GridConfigResponse,
  LegendConfig,
  LegendConfigSource,
  LegendType,
  ValueBasedLegendType
} from "@/types/project";
import type { AdminAreaSettings } from "@/types/spatial";

import {
  cacheMarkerImage,
  embedMarkerImageUrl,
  getCachedMarkerImage,
  isLocalImageUrl
} from "./mapLegend";

export type MappedFilter = {
  filterType: EFilterOptionType;
  filter: {
    criteria: string;
    include_nulls: boolean;
  } | null;
  attributeId: string;
  attributeCode: string | undefined;
  is_multiple_adm_area?: boolean;
};

export const DEFAULT_H3_CONFIG = {
  aggregation_type: "AVG" as const,
  level: 8 // Default H3 resolution level
};

export const getMaxLevelAdminArea = (
  obj: AdminAreaSettings | null
): keyof AdminAreaSettings | null => {
  if (!obj) return null;
  let maxLevel: keyof AdminAreaSettings | null = null;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const typedKey = key as keyof AdminAreaSettings;
      if (obj[typedKey] !== undefined && obj[typedKey] !== null) {
        maxLevel = typedKey;
      }
    }
  }

  return maxLevel;
};

export const mapDatasetFilters = (filter: DatasetFilter): MappedFilter => {
  let criteriaAndNulls = null;

  if (filter.filterType === EFilterOptionType.DATE_TIME) {
    criteriaAndNulls = {
      criteria:
        convertTimeRange(filter.dateTimeRange, filter.time_format) ?? "",
      include_nulls: filter.dateTimeRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.MIN_MAX) {
    criteriaAndNulls = {
      criteria: `${filter.minMaxRange?.min}-${filter.minMaxRange?.max}`,
      include_nulls: filter.minMaxRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.MULTIPLE) {
    const isAdminAreaFilter = filter.selectedOptions?.some((option) =>
      option.includes("::")
    );
    criteriaAndNulls = {
      criteria: filter.selectedOptions?.join("|") ?? "",
      include_nulls: false,
      ...(isAdminAreaFilter
        ? { is_multiple_adm_area: filter.is_multiple_adm_area ?? false }
        : {})
    };
  } else if (filter.filterType === EFilterOptionType.DATE) {
    criteriaAndNulls = {
      criteria: `${filter.dateRange?.from || null}/${filter.dateRange?.to || null}`,
      include_nulls: filter.dateRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.TIME) {
    criteriaAndNulls = {
      criteria: `${filter.timeRange?.from || null}/${filter.timeRange?.to || null}`,
      include_nulls: filter.timeRange?.includeNulls ?? false
    };
  }

  return {
    filterType: filter.filterType,
    filter: criteriaAndNulls,
    attributeId: filter.paired_key_attribute_id ?? filter.attributeId,
    attributeCode: filter.attributeCode
  };
};

export const processCatchmentDatasetAttributes = (
  dataset: Dataset,
  filters: DatasetFilter[]
): DatasetConfigAttributeResponse[] => {
  const mappedFilters: MappedFilter[] = filters.map((filter) =>
    mapDatasetFilters(filter)
  );

  /** Track matched filters to prevent the same filter from being matched to
   * multiple attributes. Admin area filters are EXCLUDED from this dedup —
   * they intentionally match every admin level (adm_area_level_1..4) so the
   * downstream `separateAdminAreaAttributes` + `createFinalAttributes` can
   * pick the lowest level (mirrors `processDatasetAttributes` for main
   * datasets). Without this, the first-match-wins dedup would lock the
   * filter to whichever admin level appears first in `dataset.categories`
   * (often the deepest), which the BE then echoes back unchanged on save.
   */
  const matchedFilterKeys = new Set<string>();

  const combinedAttributes: DatasetConfigAttributeResponse[] =
    dataset.categories?.map((attribute) => {
      const attributeId = attribute.paired_key_attribute_id ?? attribute.id;
      let matchedIsAdmArea = false;

      const matchedFilter = mappedFilters.find((f) => {
        const isAdmAreaFilter = f.filter?.criteria?.includes("::");

        if (isAdmAreaFilter) {
          if (f.attributeId === attributeId) {
            matchedIsAdmArea = true;
            return true;
          }
          return false;
        }

        const filterKey = `${f.attributeId}_${f.attributeCode ?? ""}`;
        if (matchedFilterKeys.has(filterKey)) return false;

        return (
          f.attributeId === attributeId &&
          f.attributeCode === attribute.attribute_code
        );
      }) || { filter: null, filterType: null };

      if (matchedFilter.filter && !matchedIsAdmArea) {
        const filterKey = `${(matchedFilter as MappedFilter).attributeId}_${(matchedFilter as MappedFilter).attributeCode}`;
        matchedFilterKeys.add(filterKey);
      }

      const { filter, filterType } = matchedFilter;
      return {
        filter,
        id: attributeId,
        weight_score: null,
        ...(filterType ? { filterType } : {}),
        ...(attribute.attribute_code
          ? { attribute_code: attribute.attribute_code }
          : {})
      };
    }) || [];

  /** Mirror main dataset (`processDatasetAttributes` + `separateAdminAreaAttributes`
   * + `createFinalAttributes`): collapse the admin-level entries into a single
   * primary entry at the lowest level (alphabetical) so the payload matches
   * what a non-catchment dataset with the same filter would produce.
   */
  const { adminAreaAttributes, nonAdminAttributes } =
    separateAdminAreaAttributes(combinedAttributes, dataset);

  return createFinalAttributes(nonAdminAttributes, adminAreaAttributes);
};

export const createFinalAttributes = (
  nonAdminAttributes: DatasetConfigAttributeResponse[],
  adminAreaAttributes: Map<string, DatasetConfigAttributeResponse>
): DatasetConfigAttributeResponse[] => {
  const finalAttributes = [...nonAdminAttributes];

  if (adminAreaAttributes.size > 0) {
    const sortedLevels = Array.from(adminAreaAttributes.keys()).sort((a, b) =>
      a.localeCompare(b)
    );

    const primaryAdminAttr = adminAreaAttributes.get(sortedLevels[0]);
    if (!primaryAdminAttr) return finalAttributes;

    const maxWeightScore = Math.max(
      ...Array.from(adminAreaAttributes.values()).map(
        (attr) => attr.weight_score || 0
      )
    );
    finalAttributes.push({
      ...primaryAdminAttr,
      id: primaryAdminAttr.id,
      weight_score: maxWeightScore
    });
  }

  return finalAttributes;
};

export const separateAdminAreaAttributes = (
  combinedAttributes: DatasetConfigAttributeResponse[],
  primaryDataset: Dataset
): {
  adminAreaAttributes: Map<string, DatasetConfigAttributeResponse>;
  nonAdminAttributes: DatasetConfigAttributeResponse[];
} => {
  const adminAreaAttributes = new Map<string, DatasetConfigAttributeResponse>();
  const nonAdminAttributes: DatasetConfigAttributeResponse[] = [];

  const isH3Dataset =
    primaryDataset.properties.spatialType === "H3" &&
    primaryDataset.data.source === "bvt";

  for (const attr of combinedAttributes) {
    let category: DatasetDetailSchemaResponse | undefined;
    if (isH3Dataset) {
      category = primaryDataset.data.schema.find(
        (item) => (item.paired_key_attribute_id ?? item.id) === attr.id
      );
    } else {
      category = primaryDataset.categories.find(
        (item) => (item.paired_key_attribute_id ?? item.id) === attr.id
      );
    }
    const attributeCode = category?.attribute_code;

    if (attributeCode?.startsWith("adm_area_level_")) {
      adminAreaAttributes.set(attributeCode, attr);
    } else {
      nonAdminAttributes.push(attr);
    }
  }

  return { adminAreaAttributes, nonAdminAttributes };
};

export const getGridConfig = (
  currentDataset: Dataset
): GridConfigResponse | undefined => {
  if (currentDataset?.h3Configuration) {
    return {
      aggregation_type:
        currentDataset?.h3Configuration?.aggregationType ?? "AVG",
      level: currentDataset?.h3Configuration?.resolution ?? 8
    };
  }

  return currentDataset?.properties?.spatialType === "H3" &&
    currentDataset?.data?.source === "bvt"
    ? DEFAULT_H3_CONFIG
    : undefined;
};

/** The `legend_configs` map of a style: one entry per method the user configured,
 * regardless of which method is currently active. */
export const buildLegendConfigs = (
  style: DatasetStyle | undefined,
  /** Maps a not-yet-uploaded image (data URL) to its uploaded asset id. */
  assetIdByImageUrl?: Map<string, string>,
  /** Layer opacity stored on every entry. The caller resolves it: the stroke's for
   * lines, the fill's otherwise. */
  opacity?: number
): Partial<Record<LegendType, LegendConfig>> => {
  // The last-SAVED config wins, so unsaved live previews never round-trip.
  const savedSingleStyleConfig =
    style?.singleStyleConfigSaved ?? style?.singleStyleConfig;
  const savedCustomCategoryConfig =
    style?.customCategoryConfigSaved ?? style?.customCategoryConfig;
  const savedCustomRangeConfig =
    style?.customRangeConfigSaved ?? style?.customRangeConfig;

  const resolveAssetId = (row: {
    imageUrl: string | null;
    imageAssetId: string | null;
    iconId?: string | null;
  }): string | null =>
    // A selected icon replaces any image — never persist both (BE validation
    // rejects a row with `fill_image_asset_id` AND `fill_icon_id`, ORB-3394).
    row.iconId
      ? null
      : (row.imageAssetId ??
        (row.imageUrl ? (assetIdByImageUrl?.get(row.imageUrl) ?? null) : null));

  // Each method keeps its own palette and opacity. A method never used falls back
  // to the active ones.
  const paletteOf = (legendType: LegendType): EColorNames | undefined =>
    style?.colorScaleSettings?.[legendType]?.colorFamily ?? style?.colorFamily;
  const opacityOf = (legendType: LegendType): number | undefined =>
    style?.colorScaleSettings?.[legendType]?.opacity ?? opacity;
  const stepsOf = (legendType: LegendType): number | undefined =>
    style?.colorScaleSettings?.[legendType]?.steps;

  const legendConfigs: Partial<Record<LegendType, LegendConfig>> = {};

  if (savedSingleStyleConfig) {
    legendConfigs.simple = {
      breaks: null,
      steps: null,
      default: {
        fill_color: savedSingleStyleConfig.fillColor,
        outline_color: savedSingleStyleConfig.outlineColor,
        fill_image_asset_id: resolveAssetId(savedSingleStyleConfig),
        fill_icon_id: savedSingleStyleConfig.iconId ?? null,
        // Persist the user-editable label so a rename round-trips (restore
        // falls back to the attribute name for legacy records without it).
        name: savedSingleStyleConfig.label
      },
      // Simple has a single fill, so no palette or step count of its own.
      opacity: opacityOf("simple") ?? null
    };
  }

  if (savedCustomCategoryConfig) {
    legendConfigs.custom_ordinal = {
      breaks: null,
      steps: savedCustomCategoryConfig.steps.map((step) => ({
        name: step.label,
        assigned_values: step.values,
        fill_color: step.fillColor,
        outline_color: step.outlineColor,
        fill_image_asset_id: resolveAssetId(step),
        fill_icon_id: step.iconId ?? null
      })),
      default: {
        fill_color: savedCustomCategoryConfig.otherFillColor,
        outline_color: savedCustomCategoryConfig.otherOutlineColor,
        fill_image_asset_id: null,
        fill_icon_id: null
      },
      color_scale: paletteOf("custom_ordinal") ?? null,
      // The step the user picked (an upper bound), not the row count it produced.
      color_steps:
        stepsOf("custom_ordinal") ?? savedCustomCategoryConfig.steps.length,
      opacity: opacityOf("custom_ordinal") ?? null
    };
  }

  if (savedCustomRangeConfig) {
    legendConfigs.custom_breaks = {
      steps: null,
      breaks: savedCustomRangeConfig.steps.map((step) => ({
        name: step.label,
        start: step.start,
        end: step.end,
        fill_color: step.fillColor,
        outline_color: step.outlineColor,
        fill_image_asset_id: resolveAssetId(step),
        fill_icon_id: step.iconId ?? null
      })),
      default: {
        fill_color: savedCustomRangeConfig.otherFillColor,
        outline_color: savedCustomRangeConfig.otherOutlineColor,
        fill_image_asset_id: null,
        fill_icon_id: null
      },
      color_scale: paletteOf("custom_breaks") ?? null,
      color_steps:
        stepsOf("custom_breaks") ?? savedCustomRangeConfig.steps.length,
      opacity: opacityOf("custom_breaks") ?? null
    };
  }

  return legendConfigs;
};

/** Legend fields of the save payload: the active method (Quantile/Ordinal
 * included) plus one entry per method the user configured. */
export const mapStyleToLegend = (
  style: DatasetStyle | undefined,
  assetIdByImageUrl?: Map<string, string>,
  /** Layer opacity stored on each entry — see {@link buildLegendConfigs}. */
  opacity?: number
):
  | {
      legend_type?: ActiveLegendType;
      legend_configs?: Partial<Record<LegendType, LegendConfig>>;
    }
  | undefined => {
  const method = style?.colorScaleMethod;
  const legendConfigs = buildLegendConfigs(style, assetIdByImageUrl, opacity);

  // Send the active method even when it is value-based, plus every configured
  // method, so reopening restores the same color scale and configs.
  const hasLegendConfigs = Object.keys(legendConfigs).length > 0;
  if (!method && !hasLegendConfigs) return undefined;

  return {
    ...(method ? { legend_type: method } : {}),
    ...(hasLegendConfigs ? { legend_configs: legendConfigs } : {})
  };
};

/** Whether a "Color Based On" method colours from the data instead of a config. */
const isValueBasedMethod = (
  method?: ColorScaleMethod | null
): method is ValueBasedLegendType =>
  method === "quantile" || method === "ordinal";

/** One `datasets` entry of the post-analysis legend payload — the legend fields
 * plus the color scale and opacity of one result dataset. */
export const mapDatasetToAnalysisLegendConfig = (
  datasetId: string,
  dataset: Dataset,
  assetIdByImageUrl?: Map<string, string>
): AnalysisDatasetLegendConfig => {
  const style = dataset.style;
  const method = style?.colorScaleMethod;
  // Lines carry their opacity on the stroke, other geometries on the fill.
  const opacity =
    dataset.properties?.spatialType === "line"
      ? style?.stroke?.opacity
      : style?.fillOpacity;
  const legendConfigs = buildLegendConfigs(style, assetIdByImageUrl, opacity);

  return {
    id: datasetId,
    color_scale: style?.colorFamily,
    color_steps: style?.colorSteps,
    opacity,
    // The active method, Quantile/Ordinal included, so it round-trips on reopen.
    ...(method ? { legend_type: method } : {}),
    // Every configured method is kept even while a value-based one is active, so
    // switching back after a reopen still finds its config.
    ...(Object.keys(legendConfigs).length
      ? { legend_configs: legendConfigs }
      : {})
  };
};

/** Restores every method's config from `legend_configs` into a StyleConfig, with
 * `legend_type` as the active method. Undefined when no legend fields exist. */
export const mapLegendToStyleConfig = (
  dataset: LegendConfigSource
):
  | Pick<
      StyleConfig,
      | "colorScaleMethod"
      | "colorScaleSettings"
      | "singleStyleConfig"
      | "customCategoryConfig"
      | "customRangeConfig"
    >
  | undefined => {
  // Back-compat: older records kept the active config in a singular
  // `legend_config`, so fold it into the map before reading (never sent).
  const activeType = dataset.legend_type;
  const configType = isValueBasedMethod(activeType) ? undefined : activeType;
  const legendConfigs: Partial<Record<LegendType, LegendConfig>> | null =
    dataset.legend_config && configType
      ? { ...dataset.legend_configs, [configType]: dataset.legend_config }
      : (dataset.legend_configs ?? null);
  // Nothing configured, but the active method still restores — a value-based one
  // never has a config of its own.
  if (!legendConfigs) {
    return activeType ? { colorScaleMethod: activeType } : undefined;
  }

  const result: Pick<
    StyleConfig,
    | "colorScaleMethod"
    | "colorScaleSettings"
    | "singleStyleConfig"
    | "customCategoryConfig"
    | "customRangeConfig"
  > = {};

  const simple = legendConfigs.simple;
  if (simple?.default) {
    result.singleStyleConfig = {
      fillColor: simple.default.fill_color,
      outlineColor: simple.default.outline_color,
      // Prefer the persisted label; fall back to the attribute name for
      // legacy records saved before the label was carried by the contract.
      label: simple.default.name ?? dataset.color_based?.attribute_name ?? "",
      imageUrl: simple.default.fill_image_url ?? null,
      imageName: null,
      imageAssetId: simple.default.fill_image_asset_id ?? null,
      iconId: simple.default.fill_icon_id ?? null
    };
  }

  const ordinal = legendConfigs.custom_ordinal;
  if (ordinal?.steps) {
    result.customCategoryConfig = {
      steps: ordinal.steps.map((step) => ({
        label: step.name,
        values: step.assigned_values,
        fillColor: step.fill_color,
        outlineColor: step.outline_color,
        imageUrl: step.fill_image_url ?? null,
        imageName: null,
        imageAssetId: step.fill_image_asset_id ?? null,
        iconId: step.fill_icon_id ?? null
      })),
      otherFillColor: ordinal.default.fill_color,
      otherOutlineColor: ordinal.default.outline_color
    };
  }

  const breaks = legendConfigs.custom_breaks;
  if (breaks?.breaks) {
    result.customRangeConfig = {
      steps: breaks.breaks.map((step) => ({
        label: step.name ?? "",
        start: step.start,
        end: step.end,
        fillColor: step.fill_color,
        outlineColor: step.outline_color,
        imageUrl: step.fill_image_url ?? null,
        imageName: null,
        imageAssetId: step.fill_image_asset_id ?? null,
        iconId: step.fill_icon_id ?? null
      })),
      otherFillColor: breaks.default.fill_color,
      otherOutlineColor: breaks.default.outline_color
    };
  }

  // The active method shown on open.
  if (dataset.legend_type) result.colorScaleMethod = dataset.legend_type;

  // Palette, opacity and step count each method was saved with, so switching color
  // scales after open shows that method's own look.
  const colorScaleSettings: Partial<
    Record<ColorScaleMethod, ColorScaleSettings>
  > = {};
  const rememberSaved = (
    method: LegendType,
    entry?: LegendConfig | null
  ): void => {
    const settings: ColorScaleSettings = {
      ...(entry?.color_scale ? { colorFamily: entry.color_scale } : {}),
      ...(typeof entry?.opacity === "number" ? { opacity: entry.opacity } : {}),
      ...(typeof entry?.color_steps === "number"
        ? { steps: entry.color_steps }
        : {})
    };
    if (Object.keys(settings).length) colorScaleSettings[method] = settings;
  };
  rememberSaved("simple", simple);
  rememberSaved("custom_ordinal", ordinal);
  rememberSaved("custom_breaks", breaks);
  if (Object.keys(colorScaleSettings).length)
    result.colorScaleSettings = colorScaleSettings;

  const hasAny =
    result.colorScaleMethod ||
    result.colorScaleSettings ||
    result.singleStyleConfig ||
    result.customCategoryConfig ||
    result.customRangeConfig;
  return hasAny ? result : undefined;
};

/** Presigned image URL for a marker asset id, for responses that omit the URL. */
export const resolveMarkerAssetUrl = async (
  assetId: string
): Promise<string | null> => {
  try {
    return (await getAsset(assetId)).url ?? null;
  } catch {
    return null;
  }
};

/** Merges per-method settings, later sources winning field by field. */
const mergeColorScaleSettings = (
  ...sources: (
    | Partial<Record<ColorScaleMethod, ColorScaleSettings>>
    | undefined
  )[]
): Partial<Record<ColorScaleMethod, ColorScaleSettings>> => {
  const merged: Partial<Record<ColorScaleMethod, ColorScaleSettings>> = {};
  sources.forEach((source) => {
    Object.entries(source ?? {}).forEach(([method, settings]) => {
      const key = method as ColorScaleMethod;
      merged[key] = { ...merged[key], ...settings };
    });
  });
  return merged;
};

/** Applies a restored legend over a freshly-built style: the saved config wins over
 * the generated palette and hydrates both the live and saved slots. */
export const applyRestoredLegendStyle = (
  style: DatasetStyle,
  restored?: StyleConfig
): DatasetStyle => {
  if (!restored) return style;

  const colorFamily = restored.colorFamily ?? style.colorFamily;
  const colorScaleMethod = restored.colorScaleMethod ?? style.colorScaleMethod;
  const singleStyleConfig =
    restored.singleStyleConfig ?? style.singleStyleConfig;
  const customCategoryConfig =
    restored.customCategoryConfig ?? style.customCategoryConfig;
  const customRangeConfig =
    restored.customRangeConfig ?? style.customRangeConfig;

  return {
    ...style,
    colorFamily,
    fillColor: COLORS[colorFamily][500],
    // What each method was saved with, then the active method's own palette last.
    colorScaleSettings: mergeColorScaleSettings(
      style.colorScaleSettings,
      restored.colorScaleSettings,
      colorScaleMethod ? { [colorScaleMethod]: { colorFamily } } : undefined
    ),
    colorSteps: restored.colorSteps ?? style.colorSteps,
    fillOpacity: restored.opacity ?? style.fillOpacity,
    stroke: {
      ...style.stroke,
      opacity: restored.opacity ?? style.stroke?.opacity
    },
    colorScaleMethod,
    singleStyleConfig,
    singleStyleConfigSaved:
      restored.singleStyleConfig ?? style.singleStyleConfigSaved,
    customCategoryConfig,
    customCategoryConfigSaved:
      restored.customCategoryConfig ?? style.customCategoryConfigSaved,
    customRangeConfig,
    customRangeConfigSaved:
      restored.customRangeConfig ?? style.customRangeConfigSaved
  };
};

/** A restored legend row whose marker image URL may need embedding. */
type EmbeddableRow = {
  imageUrl: string | null;
  imageAssetId?: string | null;
  iconId?: string | null;
};

const embedRowImage = async <T extends EmbeddableRow>(
  row: T,
  /** Resolves an asset id to its (presigned) URL — used when the response
   * carries only `fill_image_asset_id` and no `fill_image_url` (analysis config). */
  resolveAssetUrl?: (assetId: string) => Promise<string | null>
): Promise<T> => {
  // Icon markers carry no image to embed.
  if (row.iconId) return row;
  const cached = getCachedMarkerImage(row.imageAssetId);
  if (cached) return { ...row, imageUrl: cached };

  // Prefer the presigned `imageUrl` (project GET); otherwise resolve it from the
  // asset id (analysis config GET returns only the id, no URL).
  let sourceUrl = row.imageUrl;
  if (!sourceUrl && row.imageAssetId && resolveAssetUrl) {
    sourceUrl = await resolveAssetUrl(row.imageAssetId);
  }
  if (!sourceUrl) return row;

  const embedded = await embedMarkerImageUrl(sourceUrl);
  if (row.imageAssetId && isLocalImageUrl(embedded)) {
    cacheMarkerImage(row.imageAssetId, embedded);
  }
  return { ...row, imageUrl: embedded };
};

/**
 * Embeds each restored marker image (Simple row + every Custom Ordinal/Breaks
 * step) as a same-origin data URL so it renders on the config panel, legend and
 * — crucially — the map, whose canvas can't read a cross-origin presigned URL.
 */
export const embedLegendConfigImages = async (
  legend: ReturnType<typeof mapLegendToStyleConfig>,
  /** Resolves an asset id to its URL, for responses that omit `fill_image_url`. */
  resolveAssetUrl?: (assetId: string) => Promise<string | null>
): Promise<ReturnType<typeof mapLegendToStyleConfig>> => {
  if (!legend) return legend;

  const [singleStyleConfig, customCategoryConfig, customRangeConfig] =
    await Promise.all([
      legend.singleStyleConfig
        ? embedRowImage(legend.singleStyleConfig, resolveAssetUrl)
        : undefined,
      legend.customCategoryConfig
        ? Promise.all(
            legend.customCategoryConfig.steps.map((step) =>
              embedRowImage(step, resolveAssetUrl)
            )
          ).then((steps) => ({ ...legend.customCategoryConfig!, steps }))
        : undefined,
      legend.customRangeConfig
        ? Promise.all(
            legend.customRangeConfig.steps.map((step) =>
              embedRowImage(step, resolveAssetUrl)
            )
          ).then((steps) => ({ ...legend.customRangeConfig!, steps }))
        : undefined
    ]);

  return {
    ...legend,
    ...(singleStyleConfig && { singleStyleConfig }),
    ...(customCategoryConfig && { customCategoryConfig }),
    ...(customRangeConfig && { customRangeConfig })
  };
};
